import React, { useState } from 'react';

import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { GridApi } from 'ag-grid-community';
import { MssGridData } from 'src/hooks/analytics';

import { MetricsGrid } from '../../MetricsGrid';
import { getMssGridColDefs } from './mssGridColDefs';

/**
 * Props for the MssView component
 */
export interface MssViewProps {
    /** Optional filter text for global filtering */
    filterText?: string;
    /** Array of MSS data to display */
    data?: MssGridData[];
    /** Loading state (unused but kept for compatibility) */
    loading?: boolean;
    /** Error message (unused but kept for compatibility) */
    error?: string;
    /** Whether all data has been loaded */
    isDataFullyLoaded?: boolean;
    /** Loading completion percentage (0-100) */
    completionPercentage?: number;
    /** Service ID to determine column visibility */
    serviceId?: number;
}

/**
 * MssView component displays MSS (Market Share Score) data in a grid format
 * with simplified filtering and loading progress indicators.
 */
export const MssView: React.FC<MssViewProps> = ({
    filterText = '',
    data = [],
    isDataFullyLoaded = true,
    completionPercentage = 100,
    serviceId = 1 // Default to AMU for backward compatibility
}) => {
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    const columnDefs = getMssGridColDefs(serviceId);

    // Simple default sort model
    const defaultSortModel = [
        { colId: 'month', sort: 'desc' as const },
        { colId: 'mss', sort: 'desc' as const }
    ];

    // Simple grid ready handler
    const handleGridReady = (api: GridApi) => {
        setAgGridApi(api);
    };

    // Show no data state
    if (isDataFullyLoaded && data.length === 0) {
        return (
            <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                <Text>No MSS data available</Text>
            </Column>
        );
    }

    return (
        <Column heights={['fit', 'fill']}>
            {!isDataFullyLoaded && (
                <Row width="100%" alignmentHorizontal="center" spacing="200">
                    <Text type="b100" color="secondary">
                        Loading data... ({completionPercentage}%)
                    </Text>
                </Row>
            )}

            <MetricsGrid
                columnDefs={columnDefs}
                rowData={data}
                filterText={filterText}
                onGridReady={handleGridReady}
                defaultSortModel={defaultSortModel}
                isDataFullyLoaded={isDataFullyLoaded}
            />
        </Column>
    );
};
